import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import GlobalStyle from "./GlobalStyle";
import { FaEdit, FaFileInvoice, FaTrash } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// ---------- STYLED ----------
const columnWidths = {
  orderId: "250px",
  ritiroInsede: "100px",
  autista: "220px",
  status: "220px"
};

const MainLayout = styled.div`
  display: flex;
  height: 100vh;
`;

const PageContainer = styled.div`
  background-color: #fffdf3;
  padding: 5px;

  @media (max-width: 1024px) {
    padding: 10px;
  }

  @media (max-width: 768px) {
    padding: 10px 5px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin: 6px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px;
  }
`;

const StatusSelect = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px;
  }
`;

const StatusFeedback = styled.span`
  margin-left: 8px;
  font-size: 18px;
`;

const Button = styled.button`
  padding: 6px 10px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 12px;
    gap: 4px;
  }
`;

const DangerButton = styled(Button)`
  background: #dc3545;

  &:hover {
    background: #c82333;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  margin-bottom: 10px;

  & > * {
    flex: 1 1 160px; /* ogni filtro prende spazio minimo ma si adatta */
    min-width: 120px;
  }

  @media (max-width: 768px) {
    gap: 8px;

    & > * {
      flex: 1 1 140px;
      min-width: 100px;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 1000px;
  border-collapse: collapse;
th, td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

th {
  background-color: #f9f9f9;
  font-weight: 600;
}

tr:nth-child(even) {
  background-color: #fefefe;
}
`;

// ---------- COMPONENT ----------
const OrdersEdit = () => {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState({});
  const [statusUpdateResult, setStatusUpdateResult] = useState({});
  const [driverUpdateResult, setDriverUpdateResult] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightOrderId, setHighlightOrderId] = useState(null);
  const [scrollDone, setScrollDone] = useState(false);
  const [mostraTutti, setMostraTutti] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const ORDERS_PER_PAGE = 20;
  const navigate = useNavigate();
  const location = useLocation();
  const [pageAdjusted, setPageAdjusted] = useState(false);

useEffect(() => {
  const savedFilters = sessionStorage.getItem("ordersFilters");
  const savedMostraTutti = sessionStorage.getItem("ordersMostraTutti");
  const savedDate = sessionStorage.getItem("ordersSelectedDate");
  const savedPage = sessionStorage.getItem("ordersCurrentPage");

  if (savedFilters) setFilters(JSON.parse(savedFilters));
  if (savedMostraTutti) setMostraTutti(JSON.parse(savedMostraTutti));
  if (savedDate) setSelectedDate(new Date(savedDate));
  if (savedPage) setCurrentPage(Number(savedPage));
}, []);

const allStatusOptions = ["da comporre", "in sospeso", "da spuntare", "evaso"];
const editableStatusOptions = ["da comporre", "in sospeso", "da spuntare"]; 
  const visibleKeys =
    orders.length > 0
      ? Object.keys(orders[0]).filter(
          (key) =>
            !["createdAt", "products", "orderDate", "status", "autista"].includes(key)
        )
      : [];

  const filteredOrders = orders
    .filter((order) =>
      [...visibleKeys, "autista", "status"].every((key) =>
        filters[key]
          ? order[key]?.toString().toLowerCase().includes(filters[key].toLowerCase())
          : true
      )
    )
    .filter((order) => {
      if (mostraTutti) return true;
      const match = order.orderId.match(/\d{4}-\d{2}-\d{2}$/);
      if (!match) return false;
      const orderDate = new Date(match[0]);
      return (
        orderDate.toISOString().slice(0, 10) ===
        selectedDate.toISOString().slice(0, 10)
      );
    });

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );
  const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  // ---------- EFFETTI ----------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, driversRes] = await Promise.all([
          axios.get("/api/orders"),
          axios.get("/api/autisti"),
        ]);
        const ordersData = Array.isArray(ordersRes.data)
          ? ordersRes.data.map((o) => ({
              ...o,
              products: o.products ? (typeof o.products === 'string' ? JSON.parse(o.products) : o.products) : [],
            }))
          : [];
        setOrders(ordersData);
        setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
      } catch (err) {
        console.error("Errore caricamento dati:", err);
      }
    };
    fetchData();
  }, []);

useEffect(() => {
  sessionStorage.setItem("ordersCurrentPage", currentPage.toString());
}, [currentPage]);

useEffect(() => {
  sessionStorage.setItem("ordersFilters", JSON.stringify(filters));
  sessionStorage.setItem("ordersMostraTutti", JSON.stringify(mostraTutti));
  sessionStorage.setItem("ordersSelectedDate", selectedDate.toISOString());
}, [filters, mostraTutti, selectedDate]);










  // ripristina stato e evidenzia
useEffect(() => {
  if (!location.state) return;

  const { highlightOrderId, filters, mostraTutti, selectedDate, currentPage } = location.state;

  if (filters) setFilters(filters);
  if (mostraTutti !== undefined) setMostraTutti(mostraTutti);
  if (selectedDate) setSelectedDate(new Date(selectedDate));
  if (currentPage) setCurrentPage(currentPage);

  if (highlightOrderId) {
    setHighlightOrderId(highlightOrderId);
    setScrollDone(false);

    const timeout = setTimeout(() => {
      setHighlightOrderId(null);
    }, 5000);
    return () => clearTimeout(timeout);
  }
}, [location.state]);


useEffect(() => {
  if (highlightOrderId && orders.length > 0 && !pageAdjusted) {
    const index = filteredOrders.findIndex(o => o.orderId === highlightOrderId);
    if (index !== -1) {
      const page = Math.floor(index / ORDERS_PER_PAGE) + 1;
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    }
    setPageAdjusted(true);
  }
}, [highlightOrderId, orders, filteredOrders, currentPage, pageAdjusted]);


  // scroll automatico
useEffect(() => {
  if (highlightOrderId && !scrollDone) {
    const timeout = setTimeout(() => {
      const el = document.getElementById(`order-row-${highlightOrderId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setScrollDone(true);
    }, 150);
    return () => clearTimeout(timeout);
  }
}, [highlightOrderId, paginatedOrders, scrollDone]);



  // ---------- HANDLER ----------
  const handleFilterChange = (k, v) => {
    setFilters((prev) => ({ ...prev, [k]: v }));
    setCurrentPage(1);
    setPageAdjusted(false);
  };

  const handleStatusChange = async (id, s) => {
    try {
      await axios.post("/api/modifystatus", {
        orderId: id,
        newStatus: s,
      });
      setOrders((prev) =>
        prev.map((o) => (o.orderId === id ? { ...o, status: s } : o))
      );
      setStatusUpdateResult((p) => ({ ...p, [id]: "success" }));
    } catch {
      setStatusUpdateResult((p) => ({ ...p, [id]: "error" }));
    }
    setTimeout(() => {
      setStatusUpdateResult((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
    }, 3000);
  };


const handleDriverChange = async (id, d) => {
  const autistaValue = d === "" ? null : d;
  try {
    await axios.post("/api/modifydriver", {
      orderId: id,
      autista: autistaValue,
    });
    setOrders((prev) =>
      prev.map((o) => (o.orderId === id ? { ...o, autista: autistaValue } : o))
    );
    setDriverUpdateResult((p) => ({ ...p, [id]: "success" }));
  } catch {
    setDriverUpdateResult((p) => ({ ...p, [id]: "error" }));
  }
  setTimeout(() => {
    setDriverUpdateResult((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
  }, 3000);
};


  const handleDeleteOrder = async (id) => {
    if (!window.confirm("Sei sicuro?")) return;
    try {
      await axios.delete("/api/deleteOrder", {
        data: { orderId: id },
      });
      setOrders((p) => p.filter((o) => o.orderId !== id));
    } catch {
      alert("Errore eliminazione");
    }
  };

  const handleCreaBolla = async (id) => {
    if (!window.confirm("Creare la bolla?")) return;
    try {
      await axios.post("/api/creaBolla", {
        orderId: id,
      });
      alert("Bolla creata!");
    } catch {
      alert("Errore creazione bolla");
    }
  };

  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <h4>Gestione ordini</h4>
        <div style={{ marginBottom: "10px" }}>
<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
  <input
    type="checkbox"
    checked={mostraTutti}
    onChange={(e) => setMostraTutti(e.target.checked)}
    id="mostraTuttiCheckbox"
  />
  <label htmlFor="mostraTuttiCheckbox">Mostra tutti</label>

  {!mostraTutti && (
    <DatePicker
      selected={selectedDate}
      onChange={(d) => setSelectedDate(d || new Date())}
      dateFormat="yyyy-MM-dd"
      locale="it"
      className="custom-datepicker"
    />
  )}
</div>

          <FiltersContainer>
            <Input
              placeholder="Filtra ordine"
              value={filters.orderId || ""}
              onChange={(e) => handleFilterChange("orderId", e.target.value)}
            />
            <Input
              placeholder="Filtra sede"
              value={filters.sede || ""}
              onChange={(e) => handleFilterChange("sede", e.target.value)}
            />
            <Input
              placeholder="Sì/No"
              value={filters.ritiroInsede || ""}
              onChange={(e) => handleFilterChange("ritiroInsede", e.target.value)}
            />
            <StatusSelect
              value={filters.autista || ""}
              onChange={(e) => handleFilterChange("autista", e.target.value)}
            >
              <option value="">-- Tutti autisti --</option>
              {drivers.map((d, i) => (
                <option key={i} value={d.nome}>
                  {d.nome}
                </option>
              ))}
            </StatusSelect>

<StatusSelect
  value={filters.status || ""}
  onChange={(e) => handleFilterChange("status", e.target.value)}
>
  <option value="">-- Tutti stati --</option>
  {allStatusOptions.map((s, i) => (
    <option key={i} value={s}>
      {s}
    </option>
  ))}
</StatusSelect>

          </FiltersContainer>
        </div>
        
       <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Ordine</th>
              <th>Sede</th>
              <th>Ritiro</th>
              <th>Autista</th>
              <th>Stato</th>
              <th>Modifica</th>
              <th>Bolla</th>
              <th>Elimina</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((o, i) => (
              <tr
                key={i}
                id={`order-row-${o.orderId}`}
                style={{
                  backgroundColor:
                    o.orderId === highlightOrderId ? "#ffe082" : "transparent",
                  transition: "background-color 0.4s ease",
                }}
              >
                <td>{o.orderId}</td>
                <td>{o.sede}</td>
                <td>{o.ritiroInsede}</td>
                <td>
                  <StatusSelect
                    value={o.autista || ""}
                    onChange={(e) => handleDriverChange(o.orderId, e.target.value)}
                  >
                    <option value="">-- Autista --</option>
                    {drivers.map((d, j) => (
                      <option key={j} value={d.nome}>
                        {d.nome}
                      </option>
                    ))}
                  </StatusSelect>
                  <StatusFeedback>
                    {driverUpdateResult[o.orderId] === "success" && "✅"}
                    {driverUpdateResult[o.orderId] === "error" && "❌"}
                  </StatusFeedback>
                </td>
<td>
  <StatusSelect
    value={o.status || ""}
    onChange={(e) => handleStatusChange(o.orderId, e.target.value)}
    disabled={o.status === "evaso"} // disabilita se già evaso
  >
    {editableStatusOptions.map((s, i) => (
      <option key={i} value={s}>
        {s}
      </option>
    ))}
    {o.status === "evaso" && (
      <option value="evaso">evaso</option> // mostra ma non modificabile
    )}
  </StatusSelect>
  <StatusFeedback>
    {statusUpdateResult[o.orderId] === "success" && "✅"}
    {statusUpdateResult[o.orderId] === "error" && "❌"}
  </StatusFeedback>
</td>
                <td>
                  <Button
                    onClick={() =>
                      navigate(`/edit-products/${encodeURIComponent(o.orderId)}`, {
                        state: {
                          highlightOrderId: o.orderId,
                          filters,
                          mostraTutti,
                          selectedDate,
                          currentPage,
                        },
                      })
                    }
                  >
                    <FaEdit /> Modifica
                  </Button>
                </td>
                <td>
                  <DangerButton onClick={() => handleCreaBolla(o.orderId)}>
                    <FaFileInvoice /> Bolla
                  </DangerButton>
                </td>
                <td>
                  <DangerButton onClick={() => handleDeleteOrder(o.orderId)}>
                    <FaTrash />
                  </DangerButton>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
       </TableWrapper>

        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <Button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Precedente
          </Button>
          {[...Array(totalPages)].map((_, i) => (
            <Button
              key={i}
              style={{
                backgroundColor:
                  currentPage === i + 1 ? "#007bff" : "#28a745",
                fontWeight: currentPage === i + 1 ? "bold" : "normal",
              }}
              onClick={() => goToPage(i + 1)}
            >
              {i + 1}
            </Button>
          ))}
          <Button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Successiva →
          </Button>
        </div>
      </PageContainer>
    </>
  );
};

export default OrdersEdit;

